// src/pages/Dashboard.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import DashboardHeader from "../components/Dashboard/DashboardHeader";
import DashboardSidebar from "../components/Dashboard/DashboardSidebar";
import DashboardContent from "../components/Dashboard/DashboardContent";
import { checkAuth, handleAuthError } from "../utils/auth";

// Funções auxiliares para obter as datas iniciais
const getInitialStartDate = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return firstDay.toISOString().split("T")[0];
};

const getInitialEndDate = () => {
  const now = new Date();
  // O dia 0 do mês seguinte retorna o último dia do mês atual
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay.toISOString().split("T")[0];
};

const Dashboard = ({ setIsAuthenticated }) => {
  const [userName, setUserName] = useState("");
  const [funcao, setFuncao] = useState("");
  const [userId, setUserId] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState("home");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    totalInactiveCustomers: 0,
    totalValidQuotes: 0,
  });
  const [salesData, setSalesData] = useState([]);
  const [productData, setProductData] = useState([]);

  // Estados para os filtros, iniciando com o primeiro e último dia do mês vigente:
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedRepresentative, setSelectedRepresentative] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Cache para armazenar respostas anteriores e evitar requisições duplicadas
  const cacheRef = useRef({});
  // Flag para controlar se há uma requisição em andamento
  const isFetchingRef = useRef(false);
  // Armazena a última chave de consulta usada
  const lastQueryKeyRef = useRef("");
  // Contador para debug
  const fetchCountRef = useRef(0);

  // Atualiza layout responsivo
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Inicializa dados do usuário
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setUserName(user.name || "Usuário");
    setUserId(user.id || "");
    setFuncao(
      user.id
        ? user.id.toString()[0] === "G"
          ? "gerente"
          : "representante"
        : "representante"
    );
  }, []);

  // Função para processar os dados recebidos da API
  const processData = useCallback((data) => {
    if (data.success) {
      setStats({
        totalCustomers: data.data.metrics.totalCustomers,
        totalOrders: data.data.metrics.totalOrders,
        totalRevenue: data.data.metrics.totalRevenue,
        totalProducts: data.data.metrics.totalProducts,
        totalInactiveCustomers: data.data.metrics.totalInactiveCustomers || 0,
        totalValidQuotes: data.data.metrics.totalValidQuotes || 0,
      });

      const revenueTrends = data.data.trends.revenue;
      if (revenueTrends && revenueTrends.length > 0) {
        let filledData = [];

        // Verifica se os dados estão agrupados por mês ou por ano
        if (revenueTrends[0].month) {
          // Agrupamento mensal
          revenueTrends.sort((a, b) => a.month.localeCompare(b.month));
          const startMonth = revenueTrends[0].month;
          const endMonth = revenueTrends[revenueTrends.length - 1].month;

          let [startYear, startMon] = startMonth.split("-").map(Number);
          let [endYear, endMon] = endMonth.split("-").map(Number);
          let currentYear = startYear;
          let currentMonth = startMon;
          while (
            currentYear < endYear ||
            (currentYear === endYear && currentMonth <= endMon)
          ) {
            const monthStr = `${currentYear}-${currentMonth
              .toString()
              .padStart(2, "0")}`;
            const found = revenueTrends.find((item) => item.month === monthStr);
            if (found) {
              filledData.push(found);
            } else {
              filledData.push({
                month: monthStr,
                revenue: 0,
                orderCount: 0,
              });
            }
            currentMonth++;
            if (currentMonth > 12) {
              currentMonth = 1;
              currentYear++;
            }
          }

          setSalesData(
            filledData.map((item) => ({
              period: new Date(item.month + "-02")
                .toLocaleString("default", {
                  month: "short",
                  year: "numeric",
                })
                .replace(". de", "")
                .replace(" 20", " "),
              revenue: item.revenue,
              orders: item.orderCount,
            }))
          );
        } else if (revenueTrends[0].year) {
          // Agrupamento anual
          revenueTrends.sort((a, b) => a.year.localeCompare(b.year));
          const startYear = Number(revenueTrends[0].year);
          const endYear = Number(revenueTrends[revenueTrends.length - 1].year);
          let currentYear = startYear;
          while (currentYear <= endYear) {
            const found = revenueTrends.find(
              (item) => Number(item.year) === currentYear
            );
            if (found) {
              filledData.push(found);
            } else {
              filledData.push({
                year: currentYear.toString(),
                revenue: 0,
                orderCount: 0,
              });
            }
            currentYear++;
          }
          setSalesData(
            filledData.map((item) => ({
              period: item.year,
              revenue: item.revenue,
              orders: item.orderCount,
            }))
          );
        }
      } else {
        setSalesData([]);
      }

      setProductData(
        data.data.trends.topProducts.map((product) => ({
          code: product.code,
          name: product.name,
          quantity: product.quantity,
          image: product.image,
        }))
      );
    }
  }, []);

  // Requisição do dashboard - otimizada para evitar requisições repetidas
  const fetchDashboardData = useCallback(async () => {
    // Se já existe uma requisição em andamento, não faz nada
    if (isFetchingRef.current) return;

    // Verifica se temos todos os dados necessários
    if (!funcao) return;

    // Gera a chave para o cache baseado nos parâmetros atuais
    const queryKey = `${startDate}_${endDate}_${selectedRepresentative}_${funcao}`;

    // Se a consulta for a mesma que a última, não faz nada
    if (queryKey === lastQueryKeyRef.current) return;

    // Atualiza a última chave de consulta
    lastQueryKeyRef.current = queryKey;

    // Verifica se temos esta consulta em cache (validade de 5 minutos)
    const cachedData = cacheRef.current[queryKey];
    const now = Date.now();
    if (cachedData && now - cachedData.timestamp < 5 * 60 * 1000) {
      processData(cachedData.data);
      return;
    }

    // Marca que uma requisição está em andamento
    isFetchingRef.current = true;
    setIsLoading(true);

    // Incrementa contador para debug
    fetchCountRef.current++;

    try {
      if (!checkAuth()) {
        window.location.href = "/login";
        return;
      }
      const token = localStorage.getItem("token");

      // Constrói a URL com os filtros
      let url = `${process.env.REACT_APP_API_BASE_URL}/dashboard`;
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      // Se o usuário é gerente e selecionou um representante, envia o filtro
      if (funcao === "gerente" && selectedRepresentative)
        params.append("representative", selectedRepresentative);
      if ([...params].length > 0) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Erro ao buscar dados do dashboard");

      const data = await response.json();

      // Armazena no cache
      cacheRef.current[queryKey] = {
        data,
        timestamp: Date.now(),
      };

      // Processa os dados
      processData(data);
    } catch (error) {
      if (!handleAuthError(error)) {
        console.error("Error fetching dashboard data:", error);
      }
    } finally {
      // Marca que a requisição foi concluída
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedRepresentative, funcao, processData]);

  // Efeito para disparar a requisição quando os filtros mudarem
  useEffect(() => {
    // Uma pequena espera para evitar múltiplas chamadas durante atualizações de estado
    const timeoutId = setTimeout(() => {
      fetchDashboardData();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [fetchDashboardData]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Funções para atualizar os filtros com debounce
  const handleStartDateChange = (newDate) => {
    clearTimeout(window.startDateTimer);
    window.startDateTimer = setTimeout(() => {
      setStartDate(newDate);
    }, 500);
  };

  const handleEndDateChange = (newDate) => {
    clearTimeout(window.endDateTimer);
    window.endDateTimer = setTimeout(() => {
      setEndDate(newDate);
    }, 500);
  };

  const handleRepresentativeChange = (rep) => {
    clearTimeout(window.repTimer);
    window.repTimer = setTimeout(() => {
      setSelectedRepresentative(rep);
    }, 500);
  };

  // Função para forçar uma atualização dos dados
  const handleRefresh = useCallback(() => {
    // Limpa a última chave de consulta para forçar nova requisição
    lastQueryKeyRef.current = "";
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen overflow-hidden">
        <DashboardSidebar
          isMobile={isMobile}
          isSidebarOpen={isSidebarOpen}
          currentView={currentView}
          setCurrentView={setCurrentView}
          toggleSidebar={toggleSidebar}
        />
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          <DashboardHeader
            userName={userName}
            funcao={funcao}
            handleLogout={handleLogout}
            toggleSidebar={toggleSidebar}
            isMobile={isMobile}
            onRefresh={handleRefresh}
          />
          <main className="flex-1 overflow-auto p-4">
            {isLoading && (
              <div className="fixed top-0 left-0 w-full h-1">
                <div className="h-full bg-blue-500 animate-pulse"></div>
              </div>
            )}
            <DashboardContent
              currentView={currentView}
              stats={stats}
              salesData={salesData}
              productData={productData}
              isMobile={isMobile}
              funcao={funcao}
              gerenteId={funcao === "gerente" ? userId : ""}
              startDate={startDate}
              endDate={endDate}
              selectedRepresentative={selectedRepresentative}
              setStartDate={handleStartDateChange}
              setEndDate={handleEndDateChange}
              setSelectedRepresentative={handleRepresentativeChange}
              userId={userId}
            />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
